import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { User, ChevronDown, LogOut, Settings, Download, CreditCard, LogIn, X } from 'lucide-react';
import { useAuthStore } from '@/lib/stores/authStore';
import { usePWA } from '@/hooks/usePWA';
import { useMobile } from '@/hooks/useMobile';

interface UserMenuProps {
  onShowAuth?: () => void;
}

export function UserMenu({ onShowAuth }: UserMenuProps) {
  const { user, signOut, isAuthenticated } = useAuthStore();
  const { isInstallable, install } = usePWA();
  const isMobile = useMobile();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    if (isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);

  // Prevent body scroll when bottom sheet is open on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isOpen]);

  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
  };

  const handleInstall = async () => {
    const installed = await install();
    if (installed) {
      setIsOpen(false);
    }
  };

  const handleSignIn = () => {
    setIsOpen(false);
    onShowAuth?.();
  };

  const menuContent = (
    <div className="py-2">
      {/* Section 1: Account (logged in) or Sign In (logged out) */}
      {isAuthenticated && user ? (
        <>
          {/* Profile */}
          <button
            disabled
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-400 cursor-not-allowed"
          >
            <User className="w-4 h-4" />
            <div className="flex flex-col items-start">
              <span>Profile</span>
              <span className="text-xs text-neutral-300">{user.email}</span>
            </div>
          </button>

          {/* Settings */}
          <button
            disabled
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-400 cursor-not-allowed"
          >
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </button>

          {/* Divider before Sign Out */}
          <div className="my-2 border-t border-neutral-200" />

          {/* Sign Out */}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </>
      ) : (
        <>
          {/* Sign In */}
          <button
            onClick={handleSignIn}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-900 hover:bg-neutral-100 transition-colors"
          >
            <LogIn className="w-4 h-4" />
            <span>Log in</span>
          </button>

        </>
      )}
    </div>
  );

  return (
    <div className="relative" ref={menuRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
      >
        <User className="w-5 h-5" />
        {isAuthenticated && user ? (
          <span className="text-sm">{user.name}</span>
        ) : (
          <span className="text-sm">Account</span>
        )}
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Menu - Bottom Sheet on Mobile, Dropdown on Desktop */}
      <AnimatePresence>
        {isOpen && (
          <>
            {isMobile ? (
              /* Mobile: Bottom Sheet */
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 bg-black/40 z-40"
                  onClick={() => setIsOpen(false)}
                />
                {/* Bottom Sheet */}
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-50 max-h-[85vh] overflow-y-auto"
                >
                  {/* Handle */}
                  <div className="flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1 bg-neutral-300 rounded-full" />
                  </div>
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-100">
                    <span className="text-sm font-medium text-neutral-900">Menu</span>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-1 text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  {/* Content */}
                  {menuContent}
                  {/* Safe area padding for mobile */}
                  <div className="h-safe-area-inset-bottom pb-6" />
                </motion.div>
              </>
            ) : (
              /* Desktop: Dropdown */
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full left-0 mt-2 w-56 bg-white border border-neutral-200 rounded-xl shadow-lg z-50 overflow-hidden"
              >
                {menuContent}
              </motion.div>
            )}
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
